import type { SupportedLocale } from '@erp/shared';
export const documentProgressTranslations = {
  de: {
    title: 'Auftragsfortschritt',
    help: 'Hilfe zum Auftragsfortschritt',
    loading: 'Auftragsfortschritt wird geladen',
    error: 'Der Auftragsfortschritt konnte nicht geladen werden.',
    explanation:
      'Belegstatus und wirksame Mengen werden getrennt von Entwürfen und nächsten Aktionen angezeigt. Korrekturbelege bleiben als eigene Belege sichtbar.',
    payment: 'Zahlung',
    emptyStage: 'Noch kein Beleg',
    from: 'Aus {{number}}',
    quantities: 'Mengen je Auftragsposition',
    item: 'Auftrag / Position',
    ordered: 'Bestellt',
    reserved: 'Reserviert',
    delivered: 'Geliefert',
    remainingDelivery: 'Noch zu liefern',
    invoiced: 'Berechnet',
    remainingInvoice: 'Noch zu berechnen',
    deliveredUnbilled: 'Geliefert, nicht berechnet',
    units:
      'Alle Mengen stehen in der gespeicherten Einheit der Auftragsposition. Negative Restmengen zeigen Abweichungen.',
    next: 'Geplante nächste Aktion',
    noAction: 'Keine Folgeaktion für diesen Beleg.',
    saveFirst: 'Änderungen zuerst speichern; mögliche Kundensperren prüfen.',
    paid: 'Zugeordnet bezahlt',
    open: 'Offener Betrag',
    noPayments: 'Noch keine Zahlung zugeordnet.',
    cashDiscount: 'Skonto',
    reversed: 'Zurückgenommen',
    notQuantityBased: 'Nicht mengenbezogene Position',
    planned: 'In Entwürfen: Lieferung {{delivery}}, Rechnung {{invoice}}.',
    effects: {
      PLANNED: 'Geplant / Entwurf',
      EFFECTIVE: 'Wirksam',
      STOPPED: 'Abgebrochen / storniert',
    },
    actions: {
      ORDER: 'Auftrag erstellen',
      DELIVER: 'Restmenge liefern',
      INVOICE_DELIVERED: 'Gelieferte Mengen abrechnen',
      PAYMENT: 'Zahlung ansehen',
    },
    reasons: {
      READY: 'Bereit',
      STOPPED: 'Dieser Vorgang wurde abgebrochen oder storniert.',
      NO_REMAINDER: 'Keine zulässige Restmenge vorhanden.',
      POST_DELIVERY: 'Lieferschein zuerst versenden oder festschreiben.',
      MISSING_LINK: 'Positionszuordnung oder Lagerwirkung muss zuerst geklärt werden.',
      DRAFT_PENDING:
        'Die Restmenge ist bereits in Folgebelegentwürfen eingeplant. Öffnen Sie diese Belege.',
    },
    warnings: {
      MISSING_LINK:
        'Historische Positionsverknüpfungen fehlen. Mengen sind nicht sicher ermittelbar.',
      STOCK_UNCLEAR:
        'Lagerbuchungen und Belegstatus sind nicht eindeutig zugeordnet. Mengenwirkung prüfen.',
      OVER_DELIVERED: 'Überlieferung: die gelieferte Menge übersteigt die Bestellung.',
      OVER_INVOICED: 'Mehr berechnet als bestellt.',
      NEGATIVE_BALANCE: 'Negative wirksame Menge: Korrekturzuordnung prüfen.',
      RESERVATION_UNAVAILABLE: 'Reservierung ist nicht positionsbezogen ermittelbar.',
    },
    errors: {
      postInvoice:
        'Rechnung zuerst festschreiben. Einen Entwurf können Sie über seinen Status abbrechen.',
      unclear:
        'Mengenwirkung ist nicht eindeutig. Prüfen Sie die Positionsverknüpfungen und Lagerbuchungen der verbundenen Belege.',
      postDelivery: 'Lieferschein vor dem Abrechnen versenden oder festschreiben.',
      selection: 'Die ausgewählten Positionen oder Mengen passen nicht zum Ursprungsbeleg.',
      exceeded:
        'Die Menge überschreitet den aktuell verfügbaren Rest. Laden Sie den Beleg neu; möglicherweise wurde parallel ein Folgebeleg erstellt.',
      noRemainder:
        'Es gibt keine zulässigen Restmengen. Prüfen Sie bereits erstellte Folgebelege und Entwürfe.',
      changed: 'Der Ausgangsbeleg wurde geändert. Laden Sie ihn neu.',
      posted:
        'Die Lieferung ist bereits gebucht. Mengenänderungen benötigen einen nachvollziehbaren Korrekturvorgang.',
      referenced:
        'Diese Positionen sind bereits verknüpft oder gebucht. Mengen, Artikel und Verknüpfungen müssen erhalten bleiben.',
    },
  },
  en: {
    title: 'Order progress',
    help: 'Order progress help',
    loading: 'Loading order progress',
    error: 'Order progress could not be loaded.',
    explanation:
      'Document status and effective quantities are shown separately from drafts and next actions. Correction documents remain visible as separate documents.',
    payment: 'Payment',
    emptyStage: 'No document yet',
    from: 'From {{number}}',
    quantities: 'Quantities by order line',
    item: 'Order / line',
    ordered: 'Ordered',
    reserved: 'Reserved',
    delivered: 'Delivered',
    remainingDelivery: 'Still to deliver',
    invoiced: 'Invoiced',
    remainingInvoice: 'Still to invoice',
    deliveredUnbilled: 'Delivered, not invoiced',
    units:
      'All quantities use the saved unit of the order line. Negative remainders expose discrepancies.',
    next: 'Planned next action',
    noAction: 'No follow-up action for this document.',
    saveFirst: 'Save changes first and check any customer blocks.',
    paid: 'Allocated payments',
    open: 'Open amount',
    noPayments: 'No payment allocated yet.',
    cashDiscount: 'Cash discount',
    reversed: 'Reversed',
    notQuantityBased: 'Non-quantity line',
    planned: 'In drafts: delivery {{delivery}}, invoice {{invoice}}.',
    effects: {
      PLANNED: 'Planned / draft',
      EFFECTIVE: 'Effective',
      STOPPED: 'Stopped / cancelled',
    },
    actions: {
      ORDER: 'Create order',
      DELIVER: 'Deliver remaining quantity',
      INVOICE_DELIVERED: 'Invoice delivered quantities',
      PAYMENT: 'View payment',
    },
    reasons: {
      READY: 'Ready',
      STOPPED: 'This process was stopped or cancelled.',
      NO_REMAINDER: 'No eligible remaining quantity.',
      POST_DELIVERY: 'Ship or finalize the delivery note first.',
      MISSING_LINK: 'Clarify line links or stock effects first.',
      DRAFT_PENDING: 'The remainder is already planned in follow-up drafts. Open those documents.',
    },
    warnings: {
      MISSING_LINK: 'Historical line links are missing. Quantities cannot be determined reliably.',
      STOCK_UNCLEAR:
        'Stock postings and document status cannot be reconciled. Review their quantity effect.',
      OVER_DELIVERED: 'Overdelivery: delivered quantity exceeds the order.',
      OVER_INVOICED: 'Invoiced quantity exceeds the order.',
      NEGATIVE_BALANCE: 'Negative effective quantity: review correction links.',
      RESERVATION_UNAVAILABLE: 'Reservation cannot be determined per order line.',
    },
    errors: {
      postInvoice: 'Finalize the invoice first. A draft can be cancelled through its status.',
      unclear:
        'Quantity effects are ambiguous. Review line links and stock postings of the connected documents.',
      postDelivery: 'Ship or finalize the delivery note before invoicing.',
      selection: 'Selected lines or quantities do not match the source document.',
      exceeded:
        'Quantity exceeds the currently available remainder. Reload the document; another follow-up may have been created concurrently.',
      noRemainder:
        'No eligible remaining quantities. Review existing follow-up documents and drafts.',
      changed: 'The source document has changed. Reload it.',
      posted: 'This delivery is already posted. Quantity changes require a traceable correction.',
      referenced:
        'These lines are already linked or posted. Preserve quantities, products and links.',
    },
  },
  fr: {
    title: 'Avancement de la commande',
    help: 'Aide sur l’avancement',
    loading: 'Chargement de l’avancement',
    error: 'Impossible de charger l’avancement.',
    explanation:
      'Les statuts et quantités effectives sont séparés des brouillons et prochaines actions. Les corrections restent visibles comme documents distincts.',
    payment: 'Paiement',
    emptyStage: 'Aucun document',
    from: 'Depuis {{number}}',
    quantities: 'Quantités par ligne de commande',
    item: 'Commande / ligne',
    ordered: 'Commandé',
    reserved: 'Réservé',
    delivered: 'Livré',
    remainingDelivery: 'Reste à livrer',
    invoiced: 'Facturé',
    remainingInvoice: 'Reste à facturer',
    deliveredUnbilled: 'Livré, non facturé',
    units:
      'Les quantités utilisent l’unité enregistrée sur la commande. Les restes négatifs signalent des écarts.',
    next: 'Prochaine action prévue',
    noAction: 'Aucune action suivante pour ce document.',
    saveFirst: 'Enregistrez les modifications et vérifiez les blocages du client.',
    paid: 'Paiements affectés',
    open: 'Montant ouvert',
    noPayments: 'Aucun paiement affecté.',
    cashDiscount: 'Escompte',
    reversed: 'Annulé',
    notQuantityBased: 'Ligne sans quantité',
    planned: 'Dans les brouillons : livraison {{delivery}}, facture {{invoice}}.',
    effects: {
      PLANNED: 'Prévu / brouillon',
      EFFECTIVE: 'Effectif',
      STOPPED: 'Arrêté / annulé',
    },
    actions: {
      ORDER: 'Créer une commande',
      DELIVER: 'Livrer le reste',
      INVOICE_DELIVERED: 'Facturer les quantités livrées',
      PAYMENT: 'Voir le paiement',
    },
    reasons: {
      READY: 'Prêt',
      STOPPED: 'Ce processus a été arrêté ou annulé.',
      NO_REMAINDER: 'Aucune quantité restante admissible.',
      POST_DELIVERY: 'Expédiez ou validez d’abord le bon de livraison.',
      MISSING_LINK: 'Clarifiez les liens des lignes ou les effets sur le stock.',
      DRAFT_PENDING: 'Le reste est déjà prévu dans des brouillons suivants. Ouvrez ces documents.',
    },
    warnings: {
      MISSING_LINK:
        'Des liens historiques entre lignes manquent. Les quantités ne peuvent pas être déterminées avec certitude.',
      STOCK_UNCLEAR:
        'Les mouvements de stock et statuts ne concordent pas. Vérifiez leur effet quantitatif.',
      OVER_DELIVERED: 'La quantité livrée dépasse la commande.',
      OVER_INVOICED: 'La quantité facturée dépasse la commande.',
      NEGATIVE_BALANCE: 'Quantité effective négative : vérifiez les corrections.',
      RESERVATION_UNAVAILABLE: 'Réservation non déterminable par ligne.',
    },
    errors: {
      postInvoice: 'Validez d’abord la facture. Un brouillon peut être annulé via son statut.',
      unclear:
        'Effets quantitatifs ambigus. Vérifiez les liens et mouvements des documents associés.',
      postDelivery: 'Expédiez ou validez la livraison avant de facturer.',
      selection: 'Les lignes ou quantités choisies ne correspondent pas au document source.',
      exceeded:
        'La quantité dépasse le reste disponible. Rechargez le document ; un autre document a pu être créé simultanément.',
      noRemainder:
        'Aucune quantité restante admissible. Vérifiez les documents suivants et brouillons.',
      changed: 'Le document source a changé. Rechargez-le.',
      posted:
        'Cette livraison est comptabilisée. Les quantités nécessitent une correction traçable.',
      referenced:
        'Ces lignes sont déjà liées ou comptabilisées. Conservez quantités, articles et liens.',
    },
  },
  es: {
    title: 'Progreso del pedido',
    help: 'Ayuda del progreso',
    loading: 'Cargando el progreso',
    error: 'No se pudo cargar el progreso.',
    explanation:
      'Los estados y cantidades efectivas se muestran separados de borradores y próximas acciones. Las correcciones permanecen visibles como documentos separados.',
    payment: 'Pago',
    emptyStage: 'Aún no hay documento',
    from: 'Desde {{number}}',
    quantities: 'Cantidades por línea de pedido',
    item: 'Pedido / línea',
    ordered: 'Pedido',
    reserved: 'Reservado',
    delivered: 'Entregado',
    remainingDelivery: 'Pendiente de entregar',
    invoiced: 'Facturado',
    remainingInvoice: 'Pendiente de facturar',
    deliveredUnbilled: 'Entregado, sin facturar',
    units:
      'Las cantidades usan la unidad guardada en la línea del pedido. Los saldos negativos muestran discrepancias.',
    next: 'Próxima acción prevista',
    noAction: 'No hay acción posterior para este documento.',
    saveFirst: 'Guarde los cambios y revise los bloqueos del cliente.',
    paid: 'Pagos asignados',
    open: 'Importe pendiente',
    noPayments: 'Aún no hay pagos asignados.',
    cashDiscount: 'Descuento por pronto pago',
    reversed: 'Revertido',
    notQuantityBased: 'Línea sin cantidad',
    planned: 'En borradores: entrega {{delivery}}, factura {{invoice}}.',
    effects: {
      PLANNED: 'Previsto / borrador',
      EFFECTIVE: 'Efectivo',
      STOPPED: 'Detenido / cancelado',
    },
    actions: {
      ORDER: 'Crear pedido',
      DELIVER: 'Entregar cantidad restante',
      INVOICE_DELIVERED: 'Facturar cantidades entregadas',
      PAYMENT: 'Ver pago',
    },
    reasons: {
      READY: 'Listo',
      STOPPED: 'Este proceso se detuvo o canceló.',
      NO_REMAINDER: 'No quedan cantidades admisibles.',
      POST_DELIVERY: 'Envíe o finalice primero el albarán.',
      MISSING_LINK: 'Aclare los vínculos de líneas o los efectos en existencias.',
      DRAFT_PENDING: 'El resto ya está previsto en borradores posteriores. Abra esos documentos.',
    },
    warnings: {
      MISSING_LINK:
        'Faltan vínculos históricos entre líneas. No se pueden determinar cantidades fiables.',
      STOCK_UNCLEAR:
        'Los movimientos de existencias y estados no coinciden. Revise su efecto cuantitativo.',
      OVER_DELIVERED: 'La cantidad entregada supera el pedido.',
      OVER_INVOICED: 'La cantidad facturada supera el pedido.',
      NEGATIVE_BALANCE: 'Cantidad efectiva negativa: revise las correcciones.',
      RESERVATION_UNAVAILABLE: 'No se puede determinar la reserva por línea.',
    },
    errors: {
      postInvoice: 'Finalice primero la factura. Puede cancelar un borrador mediante su estado.',
      unclear:
        'Efectos cuantitativos ambiguos. Revise los vínculos y movimientos de los documentos relacionados.',
      postDelivery: 'Envíe o finalice el albarán antes de facturar.',
      selection: 'Las líneas o cantidades seleccionadas no coinciden con el documento original.',
      exceeded:
        'La cantidad supera el resto disponible. Recargue el documento; otro usuario puede haber creado un documento posterior.',
      noRemainder:
        'No quedan cantidades admisibles. Revise los documentos posteriores y borradores.',
      changed: 'El documento original cambió. Recárguelo.',
      posted:
        'La entrega ya está contabilizada. Cambiar cantidades requiere una corrección trazable.',
      referenced:
        'Las líneas ya están vinculadas o contabilizadas. Conserve cantidades, artículos y vínculos.',
    },
  },
  tr: {
    title: 'Sipariş ilerlemesi',
    help: 'Sipariş ilerlemesi yardımı',
    loading: 'Sipariş ilerlemesi yükleniyor',
    error: 'Sipariş ilerlemesi yüklenemedi.',
    explanation:
      'Belge durumu ve gerçekleşen miktarlar taslaklardan ve sonraki işlemlerden ayrı gösterilir. Düzeltmeler ayrı belgeler olarak görünür kalır.',
    payment: 'Ödeme',
    emptyStage: 'Henüz belge yok',
    from: 'Kaynak: {{number}}',
    quantities: 'Sipariş kalemi miktarları',
    item: 'Sipariş / kalem',
    ordered: 'Sipariş edilen',
    reserved: 'Rezerve',
    delivered: 'Teslim edilen',
    remainingDelivery: 'Teslim edilecek',
    invoiced: 'Faturalanan',
    remainingInvoice: 'Faturalanacak',
    deliveredUnbilled: 'Teslim edildi, faturalanmadı',
    units:
      'Miktarlar sipariş kaleminin kayıtlı birimindedir. Negatif kalan miktarlar farkları gösterir.',
    next: 'Planlanan sonraki işlem',
    noAction: 'Bu belge için sonraki işlem yok.',
    saveFirst: 'Önce değişiklikleri kaydedin ve müşteri engellerini kontrol edin.',
    paid: 'Atanan ödemeler',
    open: 'Açık tutar',
    noPayments: 'Henüz ödeme atanmamış.',
    cashDiscount: 'Erken ödeme iskontosu',
    reversed: 'Geri alındı',
    notQuantityBased: 'Miktara bağlı olmayan kalem',
    planned: 'Taslaklarda: teslimat {{delivery}}, fatura {{invoice}}.',
    effects: {
      PLANNED: 'Planlandı / taslak',
      EFFECTIVE: 'Gerçekleşti',
      STOPPED: 'Durduruldu / iptal',
    },
    actions: {
      ORDER: 'Sipariş oluştur',
      DELIVER: 'Kalan miktarı teslim et',
      INVOICE_DELIVERED: 'Teslim edilenleri faturalandır',
      PAYMENT: 'Ödemeyi görüntüle',
    },
    reasons: {
      READY: 'Hazır',
      STOPPED: 'Bu süreç durduruldu veya iptal edildi.',
      NO_REMAINDER: 'İşlenebilir kalan miktar yok.',
      POST_DELIVERY: 'Önce irsaliyeyi sevk edin veya kesinleştirin.',
      MISSING_LINK: 'Önce kalem bağlantılarını veya stok etkilerini netleştirin.',
      DRAFT_PENDING: 'Kalan miktar sonraki belge taslaklarında planlanmış. Bu belgeleri açın.',
    },
    warnings: {
      MISSING_LINK: 'Geçmiş kalem bağlantıları eksik. Miktarlar güvenilir biçimde belirlenemiyor.',
      STOCK_UNCLEAR: 'Stok kayıtları ile belge durumu uyuşmuyor. Miktar etkisini kontrol edin.',
      OVER_DELIVERED: 'Teslim edilen miktar siparişi aşıyor.',
      OVER_INVOICED: 'Faturalanan miktar siparişi aşıyor.',
      NEGATIVE_BALANCE: 'Negatif gerçekleşen miktar: düzeltme bağlantılarını kontrol edin.',
      RESERVATION_UNAVAILABLE: 'Rezervasyon sipariş kalemi bazında belirlenemiyor.',
    },
    errors: {
      postInvoice: 'Önce faturayı kesinleştirin. Taslağı durumundan iptal edebilirsiniz.',
      unclear:
        'Miktar etkileri belirsiz. Bağlı belgelerin kalem bağlantılarını ve stok kayıtlarını kontrol edin.',
      postDelivery: 'Faturalandırmadan önce irsaliyeyi sevk edin veya kesinleştirin.',
      selection: 'Seçilen kalem veya miktarlar kaynak belgeyle uyuşmuyor.',
      exceeded:
        'Miktar kullanılabilir kalanı aşıyor. Belgeyi yeniden yükleyin; eşzamanlı başka bir belge oluşturulmuş olabilir.',
      noRemainder: 'İşlenebilir kalan miktar yok. Sonraki belgeleri ve taslakları kontrol edin.',
      changed: 'Kaynak belge değişti. Yeniden yükleyin.',
      posted: 'Bu teslimat kaydedilmiş. Miktar değişiklikleri izlenebilir bir düzeltme gerektirir.',
      referenced:
        'Bu kalemler bağlı veya kaydedilmiş. Miktarları, ürünleri ve bağlantıları koruyun.',
    },
  },
} as const satisfies Record<SupportedLocale, object>;
